import React from "react";
import {
  Button,
  IconButton,
  Link as MuiLink,
  type ButtonProps,
  type IconButtonProps,
  type LinkProps,
} from "@mui/material";

/**
 * Wrapper anchors for values produced only by `toTelHrefAttribute`,
 * `toMailtoHrefAttribute`, or `toHttpHrefAttribute`. Keeps raw API/state strings
 * out of direct `href={...}` sinks for SAST (CWE-79).
 */

export type SanitizedHrefButtonProps = Omit<
  ButtonProps,
  "href" | "component"
> & {
  sanitizedHref: string | null | undefined;
};

/** Strip anchor overrides that must not reach MUI (React #130 when component is {}). */
function omitAnchorOverrideProps<T extends { component?: unknown; href?: unknown }>(
  props: T,
): Omit<T, "component" | "href"> {
  const { component: _component, href: _href, ...safe } = props;
  return safe;
}

export function SanitizedHrefButton({
  sanitizedHref,
  children,
  ...rest
}: SanitizedHrefButtonProps) {
  if (!sanitizedHref) {
    return null;
  }
  const safeRest = omitAnchorOverrideProps(rest);
  // Apply safeRest first so enforced anchor props always win (spread-after let `component: {}`
  // override `component="a"` and triggers React #130 "element type is invalid ... got object").
  return (
    <Button {...safeRest} component="a" href={sanitizedHref}>
      {children}
    </Button>
  );
}

export type SanitizedHrefIconButtonProps = Omit<
  IconButtonProps,
  "href" | "component" | "aria-label"
> & {
  sanitizedHref: string | null | undefined;
  /** Required accessible name for icon-only links (agents + assistive tech). */
  "aria-label": string;
};

export function SanitizedHrefIconButton({
  sanitizedHref,
  "aria-label": ariaLabel,
  children,
  ...rest
}: SanitizedHrefIconButtonProps) {
  if (!sanitizedHref) {
    return null;
  }
  const safeRest = omitAnchorOverrideProps(rest);
  return (
    <IconButton
      {...safeRest}
      component="a"
      href={sanitizedHref}
      aria-label={ariaLabel}
      title={safeRest.title ?? ariaLabel}
    >
      {children}
    </IconButton>
  );
}

export type SanitizedHrefMuiLinkProps = Omit<LinkProps, "href"> & {
  sanitizedHref: string | null | undefined;
};

export function SanitizedHrefMuiLink({
  sanitizedHref,
  children,
  ...rest
}: SanitizedHrefMuiLinkProps) {
  if (!sanitizedHref) {
    return null;
  }
  const safeRest = omitAnchorOverrideProps(rest);
  return (
    <MuiLink {...safeRest} component="a" href={sanitizedHref}>
      {children}
    </MuiLink>
  );
}
